import { GetStaticProps } from 'next';
import Image from 'next/legacy/image';
import styled from 'styled-components';

import Employee from '../backend/s3/employee';
import Flat from '../backend/s3/flat';
import Office from '../backend/s3/office';
import Testimonial from '../backend/testimonial';
import { SITE_URL } from '../common/consts';
import { IEmployee } from '../common/model/employee.model';
import { IOffice } from '../common/model/office.model';
import { ITestimonial } from '../common/model/testimonial.model';
import useI18n from '../common/hooks/useI18n';
import {
  BuyHouseOption,
  computeBuyHouseOptions,
} from '../common/helpers/buyHouseOption.utils';

import { WhereWeFrom, WhereWeGo, WhoWeAre } from '../components/nucleo';
import {
  Footer,
  Head,
  Header,
  HorizontalTimeline,
  TeamSection,
  Testimonials,
  TextSection,
} from '../components/shared';
import { useMemo } from 'react';

interface StaticProps {
  offices: IOffice[];
  buyHouseOptions: BuyHouseOption[];
  employees: IEmployee[];
  testimonials: ITestimonial[];
}

type Props = StaticProps;

const Layout = styled.div`
  display: flex;
  flex: auto;
  flex-direction: column;
  box-sizing: border-box;
  min-height: 0;

  font-style: ${(props) => props.theme.font.style};
`;

const Content = styled.main`
  position: relative;
  flex: auto;
`;

const Banner = styled.div`
  position: relative;
  width: 100%;
  height: calc(100vw / 2.89);
  max-height: 65vh;
`;

const TeamContainer = styled.div`
  padding-top: 24px;
  padding-bottom: 24px;
  padding-left: ${(props) => props.theme.grid.getGridColumns(2)};
  padding-right: ${(props) => props.theme.grid.getGridColumns(2)};
`;

const GreyBackgroundContainer = styled.div`
  background-color: ${(props) => props.theme.colors.grey};
`;

const events = [
  {
    year: 2020,
    month: 5,
    i18nKey: 'timeline2020June',
    imagesCount: 1,
  },
  {
    year: 2021,
    month: 11,
    i18nKey: 'timeline2021December',
    imagesCount: 1,
  },
  {
    year: 2022,
    month: 10,
    i18nKey: 'timeline2022November',
    imagesCount: 1,
  },
  {
    year: 2023,
    month: 2,
    i18nKey: 'timeline2023March',
    imagesCount: 2,
  },
  {
    year: 2023,
    month: 10,
    i18nKey: 'timeline2023November',
    imagesCount: 1,
  },
  {
    year: 2023,
    month: 11,
    i18nKey: 'timeline2023December',
    imagesCount: 4,
  },
  {
    year: 2024,
    month: 11,
    i18nKey: 'timeline2024December',
    imagesCount: 2,
  },
];

export const Nucleo: React.FC<Props> = ({
  offices,
  buyHouseOptions,
  employees,
  testimonials,
}) => {
  const i18n = useI18n();

  const fundingPartners = useMemo(() => {
    return employees
      .filter((e) => e.isFoundingPartner)
      .map((e) => ({
        ...e,
        position: 'SocioFundador',
      }));
  }, [employees]);
  const areaManagers = useMemo(() => {
    return employees
      .filter((e) => e.isAreaManager)
      .map((e) => ({
        ...e,
        position: e.isLadyManager ? 'JefaDeArea' : 'JefeDeArea',
      }));
  }, [employees]);

  return (
    <Layout>
      <Head
        title={i18n.t('aboutUs.title')}
        description={i18n.t('aboutUs.description')}
        canonicalUrl={`${SITE_URL}/contacto`}
      />

      <Header
        offices={offices}
        buyHouseOptions={buyHouseOptions}
        alwaysShown
        dropShadow
      />

      <Content>
        <WhoWeAre numberOfOffices={offices.length} />

        <Banner>
          <Image
            alt={i18n.t('aboutUs.title')}
            layout="fill"
            objectFit="cover"
            objectPosition={'center 70%'}
            src="/images/banner_who_are_we.jpg"
          />
        </Banner>

        <WhereWeFrom numberOfOffices={offices.length} />

        {process.env.NEXT_PUBLIC_SHOW_NUCLEO_TIMELINE === 'TRUE' && (
          <HorizontalTimeline
            title={i18n.t('aboutUs.ourHistory')}
            i18nPrefix="aboutUs"
            events={events}
          />
        )}

        <TeamContainer>
          <TextSection title={i18n.t('aboutUs.ourTeam')} />
          <br />
          <TeamSection employees={[...fundingPartners, ...areaManagers]} />
        </TeamContainer>

        <GreyBackgroundContainer>
          <Testimonials testimonials={testimonials} />
        </GreyBackgroundContainer>

        <WhereWeGo />
      </Content>

      <Footer />
    </Layout>
  );
};

export const getStaticProps: GetStaticProps<StaticProps> = async () => {
  const offices = await Office.getOffices();
  const [_, searchOptions] = await Flat.getFlats();

  const buyHouseOptions = computeBuyHouseOptions(searchOptions);

  const employees = await Employee.getEmployees();

  const testimonials = Testimonial.getTestimonials();

  return {
    props: {
      offices,
      buyHouseOptions,
      employees,
      testimonials,
    },
  };
};

export default Nucleo;
